import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./accessPermissions.scss";
import Typography from "../../../components/Typography/Typography";
import { useGetNodesListQuery } from "../../../services/supplyChainService";
import { useGetProjectsQuery } from "../../../services/projectService";
import ProjectsList from "../../../components/sct/userSettings/ProjectsList";
import NodesList from "../../../components/sct/userSettings/NodesList";
import SelectedNodesList from "../../../components/sct/userSettings/SelectedNodesList";

const AccessPermissions = ({
    onChange,
    initialSelectedNodes = {},
}) => {
    // Primary state
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedNodes, setSelectedNodes] = useState({});
    const [projectNodesCache, setProjectNodesCache] = useState({});
    const [nodesToPreload, setNodesToPreload] = useState([]);

    // Derived state
    const [selectedNodesIndicators, setSelectedNodesIndicators] = useState({});
    const [allNodesData, setAllNodesData] = useState([]);

    // Fetch projects
    const {
        data: projectsList,
        isLoading: isLoadingProjects,
        error: projectsError,
    } = useGetProjectsQuery();

    const projects = useMemo(() => projectsList?.data || [], [projectsList]);

    // Fetch nodes for selected project
    const { data: nodesList, isLoading: isLoadingNodes } = useGetNodesListQuery(
        selectedProject?.id,
        { skip: !selectedProject },
    );

    const nodeData = useMemo(() => nodesList?.data || [], [nodesList]);

    // Initialize from props
    useEffect(() => {
        if (
            initialSelectedNodes &&
            Object.keys(initialSelectedNodes).length > 0
        ) {
            setSelectedNodes(initialSelectedNodes);

            // Create indicators of which projects have selected nodes
            const indicators = {};
            const projectsToPreload = [];

            Object.keys(initialSelectedNodes).forEach((projectId) => {
                if (initialSelectedNodes[projectId]?.length > 0) {
                    indicators[projectId] =
                        initialSelectedNodes[projectId].length;
                    projectsToPreload.push(projectId);
                }
            });

            setSelectedNodesIndicators(indicators);
            setNodesToPreload(projectsToPreload);
        }
    }, [initialSelectedNodes]);

    // Manage project selection and preloading
    useEffect(() => {
        if (!projects.length) return;

        if (nodesToPreload.length > 0 && !selectedProject) {
            // Set first project to preload as the selected project
            const firstProjectId = nodesToPreload[0];
            const projectToSelect = projects.find(
                (p) => p.id === firstProjectId,
            );

            if (projectToSelect) {
                setSelectedProject(projectToSelect);
                setNodesToPreload((prev) =>
                    prev.filter((id) => id !== firstProjectId),
                );
            }
        } else if (nodesToPreload.length === 0 && !selectedProject) {
            // If no projects to preload, select the first project or one with selections
            const projectWithSelections = projects.find(
                (project) => selectedNodes[project.id]?.length > 0,
            );
            setSelectedProject(projectWithSelections || projects[0]);
        }
    }, [projects, nodesToPreload, selectedProject, selectedNodes]);

    // Handle preloading sequence
    useEffect(() => {
        if (
            nodeData.length > 0 &&
            nodesToPreload.length > 0 &&
            selectedProject
        ) {
            // Wait a moment to ensure the current project's data is cached
            const timer = setTimeout(() => {
                const nextProjectId = nodesToPreload[0];
                const nextProject = projects.find(
                    (p) => p.id === nextProjectId,
                );

                if (nextProject) {
                    setSelectedProject(nextProject);
                    setNodesToPreload((prev) =>
                        prev.filter((id) => id !== nextProjectId),
                    );
                }
            }, 300);

            return () => clearTimeout(timer);
        }
    }, [nodeData, nodesToPreload, projects, selectedProject]);

    // Cache nodes data for each project
    useEffect(() => {
        if (selectedProject && nodeData.length > 0) {
            setProjectNodesCache((prev) => {
                const newCache = {
                    ...prev,
                    [selectedProject.id]: nodeData,
                };

                // Update all cached nodes data
                const allNodes = [];
                Object.keys(newCache).forEach((projectId) => {
                    const project = projects.find((p) => p.id === projectId);
                    const projectName =
                        project?.name || project?.title || "Unknown Project";

                    newCache[projectId].forEach((node) => {
                        allNodes.push({
                            ...node,
                            projectId,
                            projectName,
                        });
                    });
                });

                setAllNodesData(allNodes);
                return newCache;
            });
        }
    }, [selectedProject, nodeData, projects]);

    // Notify parent on selections change
    useEffect(() => {
        // Update indicators
        const indicators = {};
        Object.keys(selectedNodes).forEach((projectId) => {
            if (selectedNodes[projectId]?.length > 0) {
                indicators[projectId] = selectedNodes[projectId].length;
            }
        });
        setSelectedNodesIndicators(indicators);

        // Notify parent
        if (onChange) {
            const allSelectedNodeIds = [];
            Object.values(selectedNodes).forEach((nodeIds) => {
                allSelectedNodeIds.push(...nodeIds);
            });
            onChange(allSelectedNodeIds);
        }
    }, [selectedNodes, onChange]);

    // Memoized derived data from nodeData
    const nodes = useMemo(() => {
        if (!nodeData || !selectedProject) return [];

        return nodeData.map((node) => ({
            id: node.id,
            name: node.name,
            selected:
                selectedNodes[selectedProject.id]?.includes(node.id) || false,
        }));
    }, [nodeData, selectedNodes, selectedProject]);

    // Handler functions
    const handleProjectSelect = useCallback((project) => {
        setSelectedProject(project);
    }, []);

    const handleNodeToggle = useCallback(
        (nodeId) => {
            if (!selectedProject) return;

            setSelectedNodes((prev) => {
                const projectId = selectedProject.id;
                const currentNodes = prev[projectId] || [];

                if (currentNodes.includes(nodeId)) {
                    return {
                        ...prev,
                        [projectId]: currentNodes.filter((id) => id !== nodeId),
                    };
                } else {
                    return {
                        ...prev,
                        [projectId]: [...currentNodes, nodeId],
                    };
                }
            });
        },
        [selectedProject],
    );

    const removeSelectedNode = useCallback((projectId, nodeId) => {
        setSelectedNodes((prev) => ({
            ...prev,
            [projectId]: (prev[projectId] || []).filter((id) => id !== nodeId),
        }));
    }, []);

    // Helper function to get node name from the cache
    const getNodeName = useCallback(
        (projectId, nodeId) => {
            // Check if we have cached data for this project
            if (projectNodesCache[projectId]) {
                const node = projectNodesCache[projectId].find(
                    (n) => n.id === nodeId,
                );
                if (node) return node.name;
            }

            // Check in allNodesData as a backup
            const nodeFromAll = allNodesData.find(
                (n) => n.projectId === projectId && n.id === nodeId,
            );
            if (nodeFromAll) return nodeFromAll.name;

            // Fall back to a placeholder
            return `Node ${nodeId}`;
        },
        [projectNodesCache, allNodesData],
    );

    // Get all selected nodes across all projects with names resolved from cache
    const getSelectedNodes = useMemo(() => {
        const selected = [];

        // Return empty array if no projects or node data
        if (!projects || projects.length === 0) return selected;

        // Process all selected nodes
        for (const project of projects) {
            const projectId = project.id;
            const selectedNodeIds = selectedNodes[projectId] || [];

            // Skip this project if no nodes are selected
            if (selectedNodeIds.length === 0) continue;

            // Find nodes that match selected IDs
            for (const nodeId of selectedNodeIds) {
                const nodeName = getNodeName(projectId, nodeId);

                selected.push({
                    id: `${projectId}-${nodeId}`, // Use composite key to avoid duplication
                    nodeId: nodeId,
                    name: nodeName,
                    project: project.name || project.title,
                    projectId: projectId,
                    isPlaceholder: nodeName === `Node ${nodeId}`,
                });
            }
        }

        return selected;
    }, [projects, selectedNodes, getNodeName]);

    // Loading and error states
    if (isLoadingProjects) {
        return (
            <div className="d-flex justify-content-center align-items-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">
                        {"Loading projects..."}
                    </span>
                </div>
                <span className="ms-3">{"Loading projects..."}</span>
            </div>
        );
    }

    if (projectsError) {
        return (
            <div className="p-4 alert alert-danger">
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                {`Error loading projects: ${projectsError.message}`}
            </div>
        );
    }

    return (
        <div className="container-fluid py-4 px-4 px-lg-5">
            <div className="d-flex align-items-center mb-2">
                <Typography variant="h4" className="mb-0 form-label">
                    Access Permissions
                </Typography>
            </div>

            {/* Instructions */}
            <div className="alert alert-light border mb-4">
                <div className="d-flex align-items-center">
                    <i className="bi bi-info-circle text-primary me-2"></i>
                    <Typography variant="body1" className="mb-0">
                        Select Project & Nodes — First select a project on the
                        left, then choose nodes from that project on the right
                    </Typography>
                </div>
            </div>

            <div className="row g-4">
                {/* Projects Panel */}
                <div className="col-lg-5 col-md-12">
                    <ProjectsList
                        projects={projects}
                        selectedProject={selectedProject}
                        selectedNodesIndicators={selectedNodesIndicators}
                        onProjectSelect={handleProjectSelect}
                    />
                </div>

                {/* Nodes Panel */}
                <div className="col-lg-7 col-md-12">
                    <NodesList
                        selectedProject={selectedProject}
                        nodes={nodes}
                        selectedNodes={selectedNodes}
                        isLoading={isLoadingNodes}
                        onNodeToggle={handleNodeToggle}
                    />
                </div>
            </div>

            {/* Selected Nodes */}
            <SelectedNodesList
                selectedNodes={getSelectedNodes}
                onRemoveNode={removeSelectedNode}
            />
        </div>
    );
};

export default AccessPermissions;
